import React from "react";
import styled from "styled-components";
import { FaAmazon, FaBookOpen, FaHeart } from "react-icons/fa";
import {
  colors,
  fonts,
  spacing,
  borderRadius,
  shadows,
  transitions,
} from "../styles/branding";

const BooksContainer = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  background: ${colors.gradient.primary};
  color: white;
  padding: 4rem 2rem;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  font-family: ${fonts.heading};
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  font-family: ${fonts.body};
`;

const BooksSection = styled.section`
  padding: 5rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const BooksGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4rem;
  margin-top: 3rem;
`;

const BookCard = styled.div`
  background: white;
  border-radius: ${borderRadius.md};
  overflow: hidden;
  box-shadow: ${shadows.lg};
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 400px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BookImage = styled.div`
  height: 100%;
  background: ${colors.gradient.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 4rem;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 2rem;
  }
`;

const BookContent = styled.div`
  padding: 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const BookTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 0.5rem;
  color: ${colors.inkBlack};
  font-family: ${fonts.heading};
  font-weight: 600;
`;

const BookSubtitle = styled.p`
  color: ${colors.dustyRose};
  font-weight: 500;
  margin-bottom: 1.5rem;
  font-family: ${fonts.body};
  font-size: 1.1rem;
`;

const BookDescription = styled.p`
  color: ${colors.coolTaupe};
  line-height: 1.8;
  margin-bottom: 2rem;
  font-family: ${fonts.body};
  font-size: 1.1rem;
`;

const BookFeatures = styled.ul`
  list-style: none;
  margin-bottom: 2rem;

  li {
    color: ${colors.coolTaupe};
    margin-bottom: 0.5rem;
    padding-left: 1.5rem;
    position: relative;
    font-family: ${fonts.body};

    &:before {
      content: "•";
      color: ${colors.dustyRose};
      position: absolute;
      left: 0;
    }
  }
`;

const BookButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${colors.dustyRose};
  color: white;
  padding: 1rem 2rem;
  border-radius: ${borderRadius.lg};
  text-decoration: none;
  font-weight: bold;
  font-family: ${fonts.body};
  font-size: 1.1rem;
  transition: ${transitions.normal};

  &:hover {
    background: ${colors.coolTaupe};
    transform: translateY(-2px);
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  color: ${colors.inkBlack};
  font-family: ${fonts.heading};
  font-weight: 600;
`;

const FeaturedSection = styled.section`
  padding: 5rem 2rem;
  background: ${colors.paleMauve};
`;

const FeaturedContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const FeaturedText = styled.div`
  h2 {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
    color: ${colors.inkBlack};
    font-family: ${fonts.heading};
    font-weight: 600;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: ${colors.coolTaupe};
    margin-bottom: 1.5rem;
    font-family: ${fonts.body};
  }
`;

const FeaturedImage = styled.div`
  img {
    width: 100%;
    border-radius: ${borderRadius.md};
    box-shadow: ${shadows.lg};
  }
`;

const Books = () => {
  const books = [
    {
      id: 1,
      title: "Becoming",
      subtitle: "A Journey of Self-Discovery",
      description:
        "This is my journey of identity, heartbreak, and love, told the most honest way I know how. These are my words.",
      features: [
        "Personal journey through identity",
        "Themes of heartbreak and love",
        "Honest and authentic voice",
        "Transformative poetry collection",
      ],
      image: "/images/books/becoming.jpg",
      amazonLink: "https://a.co/d/7vGQG4h",
    },
    {
      id: 2,
      title: "Half-Here",
      subtitle: "Poems of Healing and Hope",
      description:
        "Half-here is about the line between being sane and being mentally ill, being in love and being out of it, and being one identity while choosing, seeking, or being forced to take on another.",
      features: [
        "Exploration of mental health",
        "Themes of love and identity",
        "Raw and honest storytelling",
        "Perfect for those seeking understanding",
      ],
      image: "/images/books/half-here.jpg",
      amazonLink: "https://a.co/d/hrwO586",
    },
    {
      id: 3,
      title: "Love is Real",
      subtitle: "A Collection of Poems",
      description:
        "This is my journey of identity, heartbreak, and love, told the most honest way I know how. These are my words.",
      features: [
        "50+ original poems",
        "Themes of love, healing, and growth",
        "Personal reflections and insights",
        "Beautiful, accessible language",
      ],
      image: "/images/books/love-is-real.jpg",
      amazonLink: "https://a.co/d/4bYDAEG",
    },
  ];

  return (
    <BooksContainer>
      <HeroSection>
        <HeroTitle>My Published Works</HeroTitle>
        <HeroSubtitle>
          Collections of poetry that explore love, healing, and the human
          experience
        </HeroSubtitle>
      </HeroSection>

      <BooksSection>
        <BooksGrid>
          {books.map((book) => (
            <BookCard key={book.id}>
              <BookImage>
                <img src={book.image} alt={book.title} />
              </BookImage>
              <BookContent>
                <BookTitle>{book.title}</BookTitle>
                <BookSubtitle>{book.subtitle}</BookSubtitle>
                <BookDescription>{book.description}</BookDescription>
                <BookFeatures>
                  {book.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </BookFeatures>
                <BookButton
                  href={book.amazonLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaAmazon /> Get on Amazon
                </BookButton>
              </BookContent>
            </BookCard>
          ))}
        </BooksGrid>
      </BooksSection>

      <FeaturedSection>
        <FeaturedContent>
          <FeaturedText>
            <h2>Why Poetry Matters</h2>
            <p>
              Poetry has the unique ability to capture the essence of human
              experience in ways that prose cannot. Through rhythm, metaphor,
              and carefully chosen words, poetry can express the deepest
              emotions and most profound truths about what it means to be human.
            </p>
            <p>
              My collections are designed to be accessible to everyone, whether
              you're a longtime poetry lover or someone who's just beginning to
              explore the power of words. Each poem is crafted to speak to the
              heart and create moments of connection, reflection, and healing.
            </p>
            <p>
              Whether you're navigating love, loss, healing, or growth, my books
              offer companionship for your journey. They're meant to be read,
              re-read, and shared with others who might find comfort in their
              pages.
            </p>
          </FeaturedText>
          <FeaturedImage>
            <img src="/images/books/typewriter.jpg" alt="Poetry Collection" />
          </FeaturedImage>
        </FeaturedContent>
      </FeaturedSection>
    </BooksContainer>
  );
};

export default Books;
